import React, { useState, useRef, useEffect } from "react";
import { useTrans } from "../../hooks";
import PropTypes from "prop-types";
import PasswordFields from "../../shared-components/PasswordFields";
import {
  withValidation,
  FormField,
  NewCheckbox
} from "../../shared-components";
import { Button } from "@wattpad/web-ui-library";
import ReactTooltip from "react-tooltip";
import { Icon } from "../../components";
import BirthdayFields from "../authentication/BirthdayFields";

const ValidatedFormField = withValidation(FormField);

const RequestAccountDetails = ({ errorMsg, model, preservedVal = {} }) => {
  const { trans } = useTrans();
  const { email, token, type, username } = preservedVal;

  const [usernameValue, setUsernameValue] = useState();
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isBirthdayValid, setIsBirthdayValid] = useState(false);
  const [isPolicyConsentValid, setIsPolicyConsentValid] = useState(false);

  const emailRef = useRef();
  const usernameRef = useRef();
  const birthdayInput = useRef();

  const policy_agreement = trans("I have read and agree to Wattpad's <a href='/terms'>Terms of Service</a> and <a href='/privacy'>Privacy Policy</a>"); // prettier-ignore

  const isValidated =
    isEmailValid &&
    isUsernameValid &&
    isPasswordValid &&
    isBirthdayValid &&
    isPolicyConsentValid;

  const validateSignupForm = (isValid, key) => {
    if (key.toLowerCase().includes("username")) {
      setIsUsernameValid(isValid);
    } else if (key.toLowerCase().includes("email")) {
      setIsEmailValid(isValid);
    } else if (key.toLowerCase().includes("birthday")) {
      setIsBirthdayValid(isValid);
    }
  };

  const initializeFormValues = () => {
    if (email) {
      emailRef.current.value = email;
      emailRef.current.focus();
      emailRef.current.blur();
    }
    if (username) {
      usernameRef.current.value = username;
      usernameRef.current.focus();
      usernameRef.current.blur();
    } else {
      usernameRef.current.focus();
    }
  };

  useEffect(initializeFormValues, [username, email]);

  const renderLabelWithHint = (label, ariaLabel, tooltip) => (
    <div className="field-label">
      <span className="field-label-text">{label}</span>
      <div aria-label={ariaLabel} data-tip={tooltip}>
        <Icon
          height="16"
          iconName="fa-info"
          color="wp-neutral-1"
          className="tooltip-icon"
        />
      </div>
    </div>
  );

  return (
    <div className="container">
      <div
        className="onboardingModal request-account-details modal fade in"
        data-backdrop="static"
        data-keyboard="false"
        style={{ display: "block" }}
      >
        <div className="v-align-wrap">
          <div className="modal-dialog v-align">
            <div className="modal-content">
              <div className="modal-header">
                <h1 className="modal-title text-center">
                  {trans("You're almost there.")}
                </h1>
              </div>
              <div className="modal-body">
                <h2 className="text-center">
                  {trans("We need some details to keep in contact with you.")}
                </h2>
                <form
                  id="signupForm"
                  action=""
                  method="POST"
                  onSubmit={e => {
                    if (!isEmailValid) {
                      e.preventDefault();
                      emailRef.current.focus();
                    }
                    if (!isUsernameValid) {
                      e.preventDefault();
                      usernameRef.current.focus();
                    }
                    if (!isPolicyConsentValid) {
                      return false;
                    }
                  }}
                >
                  <ReactTooltip className="tool-tip" effect="solid" />

                  {renderLabelWithHint(
                    trans("Username"),
                    "username hint",
                    trans(
                      "You do not have to use your real name. You can choose to use another name to protect your privacy."
                    )
                  )}
                  <ValidatedFormField
                    name="username"
                    form="signup"
                    inputType="text"
                    title={trans("Enter username")}
                    inputRef={usernameRef}
                    validationModel={model}
                    showLabel={true}
                    onValid={key => validateSignupForm(true, key)}
                    onInvalid={key => validateSignupForm(false, key)}
                    onBlur={() => setUsernameValue(usernameRef.current?.value)}
                  />
                  <ValidatedFormField
                    name="email"
                    label={trans("Email")}
                    form="signup"
                    inputType="text"
                    title={trans("Enter email")}
                    inputRef={emailRef}
                    validationModel={model}
                    showLabel={true}
                    onValid={key => validateSignupForm(true, key)}
                    onInvalid={key => validateSignupForm(false, key)}
                  />
                  <PasswordFields
                    username={usernameValue}
                    validatePassword={setIsPasswordValid}
                  />
                  {renderLabelWithHint(
                    trans("Birthday"),
                    "why we ask for your birthday",
                    trans(
                      "You need to enter the date you were born. This information will only be visible to you and Wattpad’s Support teams."
                    )
                  )}
                  <BirthdayFields
                    inputRef={birthdayInput}
                    validationModel={model}
                    onValid={() => validateSignupForm(true, "birthday")}
                    onInvalid={() => validateSignupForm(false, "birthday")}
                  />

                  {errorMsg && (
                    <div className="alert alert-danger" role="alert">
                      {errorMsg}
                    </div>
                  )}
                  <div className="email-policy-agreement-container">
                    <NewCheckbox
                      contentId="policy-consent"
                      required
                      checked={isPolicyConsentValid}
                      onChange={() =>
                        setIsPolicyConsentValid(!isPolicyConsentValid)
                      }
                      color="ds-base-2-60"
                    />
                    <span
                      className="policy-agreement"
                      dangerouslySetInnerHTML={{
                        __html: policy_agreement
                      }}
                    />
                  </div>
                  {token && (
                    <input
                      className="token hidden"
                      readOnly
                      value={token}
                      name="token"
                    />
                  )}
                  {type && (
                    <input
                      className="account-type hidden"
                      readOnly
                      value={type}
                      name="type"
                    />
                  )}
                  <Button
                    disabled={!isValidated}
                    fullWidth={true}
                    label={trans("Continue")}
                    ariaLabel={trans("Continue")}
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

RequestAccountDetails.propTypes = {
  errorMsg: PropTypes.string,
  model: PropTypes.object,
  preservedVal: PropTypes.shape({
    email: PropTypes.string,
    token: PropTypes.string,
    type: PropTypes.string,
    username: PropTypes.string
  })
};

export default RequestAccountDetails;
